import { composerAuto, ciblePossible, CIBLE_CARTES, CIBLE_VARIE } from './compositionAuto';
import { estQcmSimple } from './questionUtils';
import { normaliserNiveau } from '../generators/ouverture';

/**
 * Règles de besoin d'un attendu (chantier composer-evaluation, SFD V4 § 5) :
 * l'état du bandeau, la composition du diagnostic, le placement en groupes
 * Fragile / En cours / Acquis, leur contenu et le découpage papier. Module pur :
 * il lit la réponse de `bilan-attendus?cible=` et reçoit les générateurs
 * (`{ hasGenerator, getCategories, generateExercice }`), testable sans écran.
 *
 * Le bilan fait foi pour les états : ses quatre listes (a_renforcer,
 * en_cours, maitrise, sans_donnee) portent déjà la règle des seuils et de la
 * note recopiée. `descente` ne contient que les prérequis bloquants, du plus
 * proche au plus lointain : rien de ce qui est sous un facilitateur.
 */

export const GROUPES = ['Fragile', 'En cours', 'Acquis'];
export const MAX_QUESTIONS_DIAGNOSTIC = 10;
/** Noyau commun aux 3 groupes (R8 amendée le 2026-10-03). */
const NB_NOYAU = 3;
/** Exercices propres à chaque groupe, en plus du noyau : 10 en tout (R8 amendée le 2026-10-11). */
const EN_PLUS = { Fragile: 7, 'En cours': 7, Acquis: 7 };
const MAX_PREREQUIS_FAIBLE = 4;
/** Exercices par prérequis de Fragile : 2 s'il est fragile pour la classe, 1 sinon. */
const PAR_PREREQUIS = { fragile: 2, autre: 1 };
/** Prérequis fragile : au moins 3 élèves à renforcer (MIN_ELEVES_FLASH). */
const SEUIL_FRAGILE = 3;
/** R16 : coups de pouce autorisés par défaut selon le groupe. */
const COUPS_DE_POUCE = { Fragile: true, 'En cours': true, Acquis: false };

const ETATS = ['a_renforcer', 'en_cours', 'maitrise'];

/** Élèves actifs du bilan : seuls eux comptent, quoi que disent les listes. */
const elevesActifs = (bilan) => (bilan?.eleves || []).map((e) => e.id);

const attenduDuBilan = (bilan, id) => (bilan?.attendus || []).find((a) => a.attendu_id === id);

/** attendu → (élève → état), limité aux élèves actifs ; « sans résultat » = absent. */
const etatsParEleve = (bilan, attenduId, actifs) => {
    const a = attenduDuBilan(bilan, attenduId);
    const etats = new Map();
    if (!a) return etats;
    ETATS.forEach((etat) => (a[etat] || []).forEach((e) => { if (actifs.has(e)) etats.set(e, etat); }));
    return etats;
};

const nbARenforcer = (bilan, attenduId, actifs) =>
    [...etatsParEleve(bilan, attenduId, actifs).values()].filter((e) => e === 'a_renforcer').length;

/**
 * État du bandeau (F2) : D, puis C, puis A, sinon B.
 * @returns {{ etat: 'A'|'B'|'C'|'D', nbEleves: number, nbAvecResultat: number,
 *   compteurs: object, date: string|null, fragiles: Array<{attenduId, nbARenforcer}> }}
 *   `nbAvecResultat` : élèves distincts ayant un résultat sur l'attendu ou sur
 *   un prérequis bloquant (le « x élèves sur N » de l'état A) ; `date` : la
 *   dernière mesure de l'attendu (D) ou des prérequis (C).
 */
export const etatBandeau = (bilan) => {
    const actifs = new Set(elevesActifs(bilan));
    const nbEleves = actifs.size;
    const moitie = nbEleves / 2;
    const descente = bilan?.descente || [];

    const surCible = etatsParEleve(bilan, bilan?.cible, actifs);
    const compteurs = { a_renforcer: 0, en_cours: 0, maitrise: 0, sans_donnee: nbEleves - surCible.size };
    surCible.forEach((etat) => { compteurs[etat] += 1; });

    const surPrerequis = new Set();
    descente.forEach((id) => etatsParEleve(bilan, id, actifs).forEach((_, e) => surPrerequis.add(e)));

    const fragiles = descente
        .map((id) => ({ attenduId: id, nbARenforcer: nbARenforcer(bilan, id, actifs) }))
        .filter((f) => f.nbARenforcer >= SEUIL_FRAGILE);
    const nbAvecResultat = new Set([...surCible.keys(), ...surPrerequis]).size;
    const dateDe = (id) => attenduDuBilan(bilan, id)?.derniere_mesure?.date || null;

    let etat = 'B';
    let date = null;
    if (nbEleves === 0) {
        // Classe sans élève actif : rien à mesurer, donc pas de bandeau (B).
        etat = 'B';
    } else if (surCible.size >= moitie) {
        etat = 'D';
        date = dateDe(bilan.cible);
    } else if (surPrerequis.size >= moitie) {
        etat = 'C';
        date = descente.map(dateDe).filter(Boolean).sort().pop() || null;
    } else if (descente.length > 0) {
        etat = 'A';
    }
    return { etat, nbEleves, nbAvecResultat, compteurs, date, fragiles };
};

/** Un exercice composé : marqué ✦ (R10), rangé comme un « + Ajouter ». */
const exerciceAuto = (attenduId, { cle, payload }) => ({
    attenduId,
    cle,
    payload: { ...payload, _meta: { ...payload._meta, exerciseurKey: cle, origine: 'auto' } },
});

/**
 * n exercices d'un attendu, QCM simples d'abord (R17) : un attendu sans aucun
 * QCM simple reçoit ses autres formats (pour 148, la carte « Inverse » en
 * saisie). Vide si l'attendu n'a pas de générateur.
 * Options : `cartes` restreint les cartes ; `tousFormats` lève R17 ;
 * `exclus` : exercices déjà composés, à ne pas redonner.
 */
const composer = (attenduId, n, { hasGenerator, getCategories, generateExercice }, { cartes: choisir = null, tousFormats = false, exclus = [] } = {}) => {
    if (!hasGenerator(attenduId)) return [];
    const toutes = getCategories(attenduId) || [];
    const cartes = choisir ? choisir(toutes) : toutes;
    const cible = !tousFormats && ciblePossible(cartes, CIBLE_CARTES) ? CIBLE_CARTES : CIBLE_VARIE;
    const { ajouts } = composerAuto({
        cartes, attenduId, cible, n, dejaPris: null, generateExercice, exclus: exclus.map((e) => e.payload),
    });
    return ajouts.map((ajout) => exerciceAuto(attenduId, ajout));
};

/**
 * Cartes complexes d'un attendu (R15 rétablie le 2026-10-03) : celles qui ont
 * le plus d'`etapes` parmi les cartes annotées. Aucune si aucune carte n'est
 * annotée, ou si toutes ont le même nombre d'étapes (rien ne se détache).
 */
export const cartesComplexes = (cartes) => {
    const annotees = (cartes || []).filter((c) => Number.isInteger(c.etapes));
    if (annotees.length === 0) return [];
    const max = Math.max(...annotees.map((c) => c.etapes));
    return annotees.every((c) => c.etapes === max) ? [] : annotees.filter((c) => c.etapes === max);
};

/**
 * Diagnostic (R5) : une question par prérequis bloquant de la descente, dans
 * son ordre, 10 au plus. Un prérequis sans générateur est ignoré et signalé.
 * Deux versions (R5 amendée le 2026-10-03) : `CIBLE_CARTES`, QCM simples
 * seulement, un prérequis sans QCM simple est écarté sans être signalé
 * (amendement du 2026-10-04) ; `CIBLE_VARIE`, tous types, pour le papier ou
 * l'en ligne.
 * @returns {{ questions: Array, ignores: number[], cartesPossibles: boolean }}
 */
export const composerDiagnostic = (bilan, generateurs, version = CIBLE_CARTES) => {
    const questions = [];
    const ignores = [];
    for (const id of bilan?.descente || []) {
        if (questions.length >= MAX_QUESTIONS_DIAGNOSTIC) break;
        const [q] = composer(id, 1, generateurs, { tousFormats: version === CIBLE_VARIE });
        if (!q) ignores.push(id);
        else if (version === CIBLE_VARIE || estQcmSimple(q.payload)) questions.push(q);
    }
    return {
        questions,
        ignores,
        cartesPossibles: questions.length > 0 && questions.every((q) => estQcmSimple(q.payload)),
    };
};

/**
 * Diagnostic rangé du niveau le plus bas au plus haut (R5 amendée le 2026-10-04).
 */
const RANG_NIVEAU = { '6eme': 0, '5eme': 1, '4eme': 2, '3eme': 3 };
export const ordonnerParNiveau = (questions, infos) => {
    const rang = (q) => RANG_NIVEAU[normaliserNiveau(infos?.[q.attenduId]?.niveau)] ?? 4; // inconnu : en dernier
    return [...questions].sort((a, b) => rang(a) - rang(b));
};

/**
 * ↻ Autre question d'un prérequis du diagnostic (tiroir, 2026-10-04) : la
 * carte suivante du prérequis d'abord, sinon la même carte avec un autre
 * énoncé ; `null` si rien de neuf. Version cartes : QCM simples seulement.
 */
export const autreQuestion = (attenduId, enPlace, version, generateurs) => {
    const tousFormats = version === CIBLE_VARIE;
    const carteDe = (cartes) => cartes.find((c) => enPlace?.cle?.startsWith(`${attenduId}_${c.id}_`));
    const suivantes = (cartes) => {
        const i = cartes.indexOf(carteDe(cartes));
        return [...cartes.slice(i + 1), ...cartes.slice(0, Math.max(i, 0))]
            .filter((c) => tousFormats || ciblePossible([c], CIBLE_CARTES));
    };
    const [autre] = composer(attenduId, 1, generateurs, { cartes: suivantes, tousFormats });
    if (autre) return autre;
    const [memeCarte] = composer(attenduId, 1, generateurs, {
        cartes: (cartes) => [carteDe(cartes)].filter(Boolean), tousFormats, exclus: [enPlace],
    });
    return memeCarte || null;
};

/**
 * Placement en 3 groupes (R7), sans moyenne entre attendus.
 * `base` = 'prerequis' (R7a, pire état parmi les prérequis bloquants mesurés)
 * ou 'attendu' (R7b, état sur la cible). Sans résultat → En cours, marqué.
 * @returns {{ groupes: { Fragile: number[], 'En cours': number[], Acquis: number[] }, sansResultat: number[] }}
 */
export const placerGroupes = (bilan, base) => {
    const actifsListe = elevesActifs(bilan);
    const actifs = new Set(actifsListe);
    const sources = base === 'prerequis' ? (bilan?.descente || []) : [bilan?.cible];
    const parAttendu = sources.map((id) => etatsParEleve(bilan, id, actifs));
    const groupes = Object.fromEntries(GROUPES.map((g) => [g, []]));
    const sansResultat = [];
    actifsListe.forEach((e) => {
        const etats = new Set(parAttendu.map((m) => m.get(e)).filter(Boolean));
        if (etats.has('a_renforcer')) groupes.Fragile.push(e);
        else if (etats.has('en_cours')) groupes['En cours'].push(e);
        else if (etats.has('maitrise')) groupes.Acquis.push(e);
        else { groupes['En cours'].push(e); sansResultat.push(e); }
    });
    return { groupes, sansResultat };
};

/**
 * Prérequis à travailler par un groupe (R8) : ceux de la descente où au moins
 * un de ses élèves n'est pas maîtrisé (sans résultat compris), les fragiles
 * de la classe d'abord, puis du plus proche au plus lointain ; 4 au plus.
 */
export const prerequisATravailler = (bilan, eleves) => {
    const actifs = new Set(elevesActifs(bilan));
    const aTravailler = (bilan?.descente || []).filter((id) => {
        const etats = etatsParEleve(bilan, id, actifs);
        return eleves.some((e) => actifs.has(e) && etats.get(e) !== 'maitrise');
    });
    // Tri stable : à fragilité égale, l'ordre de la descente (le plus proche d'abord).
    return aTravailler
        .map((id) => ({ id, fragile: nbARenforcer(bilan, id, actifs) >= SEUIL_FRAGILE }))
        .sort((a, b) => b.fragile - a.fragile)
        .slice(0, MAX_PREREQUIS_FAIBLE)
        .map((p) => p.id);
};

/**
 * Contenu des groupes (R8 amendée, R15, R16) autour d'un noyau commun :
 * - noyau : 3 exercices de l'attendu, QCM simples d'abord, générés une fois et
 *   partagés par les 3 groupes ;
 * - 10 exercices par groupe (R8 amendée le 2026-10-11) ;
 * - Fragile : ses prérequis à travailler (4 au plus, 2 exercices pour un
 *   prérequis fragile, 1 sinon), le noyau, puis l'attendu jusqu'à 10 ;
 * - En cours : le noyau, puis 7 autres exercices de l'attendu ;
 * - Acquis : le noyau, puis 7 exercices complexes (`cartesComplexes`), tous
 *   formats, l'attendu complétant s'il en manque ; `sansComplexe` si l'attendu
 *   n'en a pas (7 exercices de l'attendu, et la colonne le dit).
 * Les exercices en plus sont propres à leur groupe et distincts du noyau.
 * Le contenu est le même que le placement vienne de R7a ou de R7b.
 * `manque` : exercices prévus pour le groupe (noyau compris, 10 en tout) qui
 * n'ont pas pu être composés, faute d'instances distinctes.
 * @returns {Array<{ nom, eleves, sansResultat, coupsDePouce, prerequis, noyau, propres, exercices, manque, sansComplexe }>}
 */
export const composerGroupes = (bilan, placement, generateurs) => {
    const cible = bilan?.cible;
    const noyau = composer(cible, NB_NOYAU, generateurs);
    const manqueNoyau = NB_NOYAU - noyau.length;
    const sansComplexe = !generateurs.hasGenerator(cible) || cartesComplexes(generateurs.getCategories(cible)).length === 0;
    const sansResultat = new Set(placement.sansResultat);
    return GROUPES.map((nom) => {
        const eleves = placement.groupes[nom];
        const prerequis = nom === 'Fragile' ? prerequisATravailler(bilan, eleves) : [];
        const enPlus = EN_PLUS[nom] || 0;
        let propres;
        if (nom === 'Fragile') {
            const actifs = new Set(elevesActifs(bilan));
            const prerequisComposes = prerequis.flatMap((id) => composer(id, Math.min(
                nbARenforcer(bilan, id, actifs) >= SEUIL_FRAGILE ? PAR_PREREQUIS.fragile : PAR_PREREQUIS.autre,
                enPlus), generateurs)).slice(0, enPlus);
            propres = [...prerequisComposes,
                ...composer(cible, enPlus - prerequisComposes.length, generateurs, { exclus: noyau })];
        } else if (nom === 'Acquis') {
            // Complexes d'abord, puis l'attendu complète jusqu'au total.
            const complexes = sansComplexe ? [] : composer(cible, enPlus, generateurs, { exclus: noyau, cartes: cartesComplexes, tousFormats: true });
            propres = [...complexes, ...composer(cible, enPlus - complexes.length, generateurs, { exclus: [...noyau, ...complexes] })];
        } else {
            propres = composer(cible, enPlus, generateurs, { exclus: noyau });
        }
        const manquePropres = enPlus - propres.length;
        return {
            nom,
            eleves,
            sansResultat: eleves.filter((e) => sansResultat.has(e)),
            coupsDePouce: COUPS_DE_POUCE[nom],
            prerequis,
            noyau,
            propres,
            exercices: exercicesDuGroupe(propres, noyau, cible, (p) => p),
            manqueNoyau,
            manquePropres,
            manque: manqueNoyau + manquePropres,
            sansComplexe: nom === 'Acquis' && sansComplexe,
        };
    });
};

/** Exercices de l'attendu qui n'ont pas pu être composés, le noyau compté une fois. */
export const manqueTotal = (groupes) =>
    (groupes?.[0]?.manqueNoyau || 0) + (groupes || []).reduce((n, g) => n + g.manquePropres, 0);

/**
 * Sujet d'un groupe dans l'ordre (composition, papier, en ligne) : ses
 * prérequis propres, puis les exercices communs (noyau ou panier), puis ses
 * exercices propres de l'attendu. `propres` : `{ attenduId, payload }` ;
 * `prendre` extrait ce qui va dans le sujet (le payload par défaut).
 */
export const exercicesDuGroupe = (propres, communs, cible, prendre = (p) => p.payload) => [
    ...propres.filter((p) => p.attenduId !== cible).map(prendre),
    ...communs,
    ...propres.filter((p) => p.attenduId === cible).map(prendre),
];

/** Un groupe vide reste affiché mais ne produit aucune diffusion (R7). */
export const groupesADiffuser = (groupes) => (groupes || []).filter((g) => g.eleves.length > 0);

/**
 * Découpage papier (F7) : un sujet par groupe non vide, dans l'ordre, sous un
 * code neutre ; la planche de coups de pouce suit le réglage du groupe ; le
 * récapitulatif prof donne le sujet de chaque élève.
 * @returns {{ sujets: Array<{ numero, titre, eleves, exercices, plancheAides }>, recapitulatif: Array<{ eleveId, sujet }> }}
 */
export const decouperPapier = (groupes) => {
    const sujets = groupesADiffuser(groupes).map((g, i) => ({
        numero: i + 1,
        titre: `Sujet ${i + 1}`,
        eleves: g.eleves,
        exercices: g.exercices,
        plancheAides: g.coupsDePouce,
    }));
    const recapitulatif = sujets.flatMap((s) => s.eleves.map((eleveId) => ({ eleveId, sujet: s.numero })));
    return { sujets, recapitulatif };
};
